import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import {
    Clock,
    TrendingUp,
    Zap,
    BarChart3,
    Calendar,
    Filter,
    RefreshCw,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Layers,
    ArrowUpRight,
    Sun,
    Hash,
    Percent
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    Cell,
    Line
} from 'recharts';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const PRESETS = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'this_week', label: 'This Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'last_5_months', label: 'Last 5 Months' },
    { key: 'last_12_months', label: 'Last 12 Months' },
    { key: 'last_year', label: 'Last Year' },
    { key: 'all', label: 'All Time' },
    { key: 'custom', label: 'Custom Range' }
];

const DEV_OPTIONS = [
    { key: 'exclude', label: 'Exclude Developers' },
    { key: 'include', label: 'Include All' },
    { key: 'only', label: 'Developers Only' }
];

const STATUS_OPTIONS = [
    { key: 'all', label: 'All Trips' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' }
];

const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
};

const PeakHours = () => {
    const { hasPermission } = useAuth();
    const canView = hasPermission('peakhours', 'view');

    const [preset, setPreset] = useState('last_12_months');
    const [developerFilter, setDeveloperFilter] = useState('exclude');
    const [tripStatus, setTripStatus] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [concurrencyWindow, setConcurrencyWindow] = useState(2);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 7;

    const timezoneOffset = new Date().getTimezoneOffset() * -1; // browser offset in minutes

    const handleReset = () => {
        setPreset('last_12_months');
        setDeveloperFilter('exclude');
        setTripStatus('all');
        setCustomStartDate('');
        setCustomEndDate('');
        setShowFilters(false);
        setConcurrencyWindow(2);
        setCurrentPage(1);
    };

    const queryParams = useMemo(() => {
        const params = {
            developerFilter,
            tripStatus,
            timezoneOffset,
            concurrencyWindow
        };
        if (preset === 'custom' && customStartDate && customEndDate) {
            params.startDate = customStartDate;
            params.endDate = customEndDate;
        } else if (preset !== 'custom') {
            params.preset = preset;
        }
        return params;
    }, [preset, developerFilter, tripStatus, customStartDate, customEndDate, timezoneOffset, concurrencyWindow]);

    const { data: peakData, isLoading, error, refetch } = useQuery(
        ['peak-hours', queryParams],
        () => api.get('/peak-hours', { params: queryParams }),
        {
            staleTime: 60000,
            refetchOnWindowFocus: false,
            enabled: canView && (preset !== 'custom' || (!!customStartDate && !!customEndDate))
        }
    );

    if (error) {
        toast.error('Failed to load peak hour data');
    }

    const result = peakData?.data?.data;
    const peakHour = result?.peakHour;
    const filters = result?.filters;
    const hourlyDistribution = result?.hourlyDistribution || [];
    const dailyPeakTrend = result?.dailyPeakTrend || [];
    const overallPeakHour = result?.overallPeakHour;
    const totalMaxConcurrent = result?.totalMaxConcurrent || 0;
    const windowMin = result?.concurrencyWindowMinutes || concurrencyWindow;

    // Reset page to 1 if trend data length changes or query changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [queryParams]);

    // Pagination logic
    const totalPages = Math.ceil(dailyPeakTrend.length / ITEMS_PER_PAGE);
    const paginatedTrend = dailyPeakTrend.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Find max count for chart highlighting
    const maxCount = Math.max(...hourlyDistribution.map(h => h.count), 0);

    // Prepare chart data
    const peakHourOfDay = peakHour
        ? Math.floor(((peakHour.startTime % 86400000) + timezoneOffset * 60000) / 3600000) % 24
        : -1;

    const chartData = hourlyDistribution.map(h => ({
        ...h,
        label: formatHour(h.hour),
        isPeak: peakHour && h.hour === peakHourOfDay
    }));

    // Daily trend chart data
    const trendData = dailyPeakTrend.map(d => ({
        date: d._id,
        peakHour: d.peakHour,
        peakCount: d.peakCount,
        totalTrips: d.totalTrips,
        label: formatHour(d.peakHour)
    }));

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        return (
            <div className="bg-gray-900 text-white rounded-lg px-4 py-3 shadow-xl text-sm border border-gray-700/50">
                <p className="font-semibold text-indigo-300 mb-1">{label}</p>
                {payload.map((entry, index) => (
                    <p key={index} className="text-gray-200">
                        <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
                        {entry.name}: <span className="font-medium text-white">{entry.value.toLocaleString()}</span>
                    </p>
                ))}
            </div>
        );
    };

    const TrendTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        const item = payload[0]?.payload;
        return (
            <div className="bg-gray-900 text-white rounded-lg px-4 py-3 shadow-xl text-sm border border-gray-700/50">
                <p className="font-semibold text-indigo-300 mb-1">{label}</p>
                <p className="text-gray-300">Peak Hour: <span className="text-white font-medium">{item?.label}</span></p>
                <p className="text-gray-300">Peak Trips: <span className="text-white font-medium">{item?.peakCount}</span></p>
                <p className="text-gray-300">Total Trips: <span className="text-white font-medium">{item?.totalTrips}</span></p>
            </div>
        );
    };

    if (!canView) {
        return (
            <div className="empty-state">
                <Clock className="empty-state-icon" />
                <h3 className="empty-state-title">Access Denied</h3>
                <p className="empty-state-description">
                    You don't have permission to view peak hour analysis.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* ── Page Header ─────────────────────────────────────────────── */}
            <div className="page-header">
                <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
                    <div>
                        <h1 className="page-title flex items-center gap-2">
                            <Zap className="h-6 w-6 text-indigo-500" />
                            Peak Hour Analysis
                        </h1>
                        <p className="page-subtitle">
                            Identify the busiest hours and concurrency patterns across your ride data
                        </p>
                    </div>
                    <button
                        onClick={handleReset}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors text-sm font-medium shadow-lg shadow-indigo-500/25"
                    >
                        <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* ── Filters ─────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Filter Toggle Button */}
                <button
                    id="filter-toggle"
                    onClick={() => setShowFilters(!showFilters)}
                    className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Filter className="h-4 w-4 text-indigo-500" />
                        Filters
                        {filters && (
                            <span className="text-xs text-gray-500 ml-2">
                                {filters.startFormatted} – {filters.endFormatted} · {developerFilter === 'exclude' ? 'Excl. Devs' : developerFilter === 'only' ? 'Devs Only' : 'All Users'} · {tripStatus === 'all' ? 'All Statuses' : tripStatus}
                            </span>
                        )}
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>

                {showFilters && (
                    <div className="px-5 py-4 space-y-4 border-t border-gray-100">
                        {/* Time Presets */}
                        <div>
                            <div className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Time Range</div>
                            <div className="flex flex-wrap gap-2">
                                {PRESETS.map((p) => (
                                    <button
                                        key={p.key}
                                        id={`preset-${p.key}`}
                                        onClick={() => setPreset(p.key)}
                                        className={`px-3.5 py-1.5 text-sm rounded-lg font-medium transition-all ${preset === p.key
                                            ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/25'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            {preset === 'custom' && (
                                <div className="flex items-center gap-3 mt-3">
                                    <input
                                        id="custom-start-date"
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                    />
                                    <span className="text-gray-400 text-sm">to</span>
                                    <input
                                        id="custom-end-date"
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Developer Filter & Trip Status */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">User Type</div>
                                <div className="flex flex-wrap gap-2">
                                    {DEV_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.key}
                                            id={`dev-filter-${opt.key}`}
                                            onClick={() => setDeveloperFilter(opt.key)}
                                            className={`px-3.5 py-1.5 text-sm rounded-lg font-medium transition-all ${developerFilter === opt.key
                                                ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Trip Status</div>
                                <div className="flex flex-wrap gap-2">
                                    {STATUS_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.key}
                                            id={`status-filter-${opt.key}`}
                                            onClick={() => setTripStatus(opt.key)}
                                            className={`px-3.5 py-1.5 text-sm rounded-lg font-medium transition-all ${tripStatus === opt.key
                                                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/25'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Concurrency Window */}
                        <div>
                            <div className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Simultaneous Window</div>
                            <div className="flex items-center gap-3">
                                <div className="flex flex-wrap gap-2">
                                    {[1, 2, 3, 5, 10].map((min) => (
                                        <button
                                            key={min}
                                            id={`window-${min}`}
                                            onClick={() => setConcurrencyWindow(min)}
                                            className={`px-3.5 py-1.5 text-sm rounded-lg font-medium transition-all ${concurrencyWindow === min
                                                ? 'bg-rose-600 text-white shadow-md shadow-rose-500/25'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {min} min
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        id="custom-window"
                                        type="number"
                                        min="1"
                                        max="60"
                                        value={concurrencyWindow}
                                        onChange={(e) => setConcurrencyWindow(Math.max(1, Math.min(60, parseInt(e.target.value) || 1)))}
                                        className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none"
                                    />
                                    <span className="text-xs text-gray-500">min</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Loading State ───────────────────────────────────────────── */}
            {isLoading && (
                <div className="flex items-center justify-center py-20">
                    <div className="flex flex-col items-center gap-3">
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full border-4 border-indigo-200 animate-spin" style={{ borderTopColor: '#6366f1' }}></div>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">Analyzing peak hours…</p>
                    </div>
                </div>
            )}

            {/* ── No Data State ───────────────────────────────────────────── */}
            {!isLoading && !peakHour && (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
                    <BarChart3 className="h-16 w-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700">No Trip Data Found</h3>
                    <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or selecting a different time range.</p>
                </div>
            )}

            {/* ── Peak Hour Summary Cards ─────────────────────────────────── */}
            {!isLoading && peakHour && (
                <>
                    {/* ── Overall Peak Hour Banner ───────────────────────────── */}
                    {overallPeakHour && (
                        <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-xl p-6 text-white shadow-2xl border border-indigo-500/20">
                            {/* Decorative elements */}
                            <div className="absolute top-0 left-1/4 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-32 blur-3xl"></div>
                            <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-violet-500/10 rounded-full translate-y-20 blur-3xl"></div>
                            <div className="absolute top-2 right-4 text-indigo-500/20">
                                <Sun className="h-24 w-24" />
                            </div>

                            <div className="relative z-10">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="p-2 bg-indigo-500/20 rounded-lg border border-indigo-500/30 backdrop-blur-sm">
                                        <Sun className="h-5 w-5 text-indigo-400" />
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold uppercase tracking-widest text-indigo-400">Overall Peak Hour</span>
                                        <p className="text-xs text-slate-400">Busiest hour-of-day across the entire selected period</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-4">
                                    {/* Time Window */}
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-indigo-500/15 rounded-xl border border-indigo-500/20">
                                            <Clock className="h-7 w-7 text-indigo-400" />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold tracking-tight text-white">
                                                {overallPeakHour.startFormatted}
                                            </p>
                                            <p className="text-sm text-slate-400">
                                                to {overallPeakHour.endFormatted}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Total Trips */}
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-emerald-500/15 rounded-xl border border-emerald-500/20">
                                            <Hash className="h-7 w-7 text-emerald-400" />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold tracking-tight text-white">
                                                {overallPeakHour.totalTrips.toLocaleString()}
                                            </p>
                                            <p className="text-sm text-slate-400">
                                                total trips at this hour
                                            </p>
                                        </div>
                                    </div>

                                    {/* Avg Per Day */}
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-amber-500/15 rounded-xl border border-amber-500/20">
                                            <TrendingUp className="h-7 w-7 text-amber-400" />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold tracking-tight text-white">
                                                {overallPeakHour.avgTripsPerDay}
                                            </p>
                                            <p className="text-sm text-slate-400">
                                                avg trips/day at this hour
                                            </p>
                                        </div>
                                    </div>

                                    {/* % of Total */}
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-violet-500/15 rounded-xl border border-violet-500/20">
                                            <Percent className="h-7 w-7 text-violet-400" />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold tracking-tight text-white">
                                                {overallPeakHour.percentOfTotal}%
                                            </p>
                                            <p className="text-sm text-slate-400">
                                                of all {overallPeakHour.totalTripsInRange.toLocaleString()} trips
                                            </p>
                                        </div>
                                    </div>

                                    {/* Simultaneous Trips — total across entire range */}
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-rose-500/15 rounded-xl border border-rose-500/20">
                                            <Layers className="h-7 w-7 text-rose-400" />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold tracking-tight text-white">
                                                {totalMaxConcurrent.toLocaleString()}
                                            </p>
                                            <p className="text-sm text-slate-400">
                                                max simultaneous ({windowMin} min)
                                            </p>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                across entire period
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Specific Peak Slot Cards ──────────────────────────── */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {/* Peak Hour Card */}
                        <div className="relative overflow-hidden bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl p-5 text-white shadow-xl shadow-indigo-500/20">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-6 -translate-x-6"></div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                        <Clock className="h-5 w-5" />
                                    </div>
                                    <span className="text-xs font-semibold uppercase tracking-wider text-indigo-200">Peak Hour</span>
                                </div>
                                <p className="text-3xl font-bold tracking-tight">
                                    {peakHour.startFormatted}
                                </p>
                                <p className="text-sm text-indigo-200 mt-1">
                                    to {peakHour.endFormatted}
                                </p>
                                <p className="text-xs text-indigo-300 mt-2 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {peakHour.dateFormatted}
                                </p>
                            </div>
                        </div>

                        {/* Trip Count Card */}
                        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl p-5 text-white shadow-xl shadow-emerald-500/20">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                        <TrendingUp className="h-5 w-5" />
                                    </div>
                                    <span className="text-xs font-semibold uppercase tracking-wider text-emerald-200">Trips in Peak Hour</span>
                                </div>
                                <p className="text-3xl font-bold tracking-tight">{peakHour.tripCount.toLocaleString()}</p>
                                <p className="text-sm text-emerald-200 mt-1">Bookings recorded</p>
                            </div>
                        </div>

                        {/* Utilisation / Date Range */}
                        <div className="relative overflow-hidden bg-gradient-to-br from-violet-500 to-purple-700 rounded-xl p-5 text-white shadow-xl shadow-violet-500/20">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                                        <ArrowUpRight className="h-5 w-5" />
                                    </div>
                                    <span className="text-xs font-semibold uppercase tracking-wider text-violet-200">Analysis Period</span>
                                </div>
                                <p className="text-lg font-bold tracking-tight">{filters?.startFormatted}</p>
                                <p className="text-sm text-violet-200 mt-0.5">to {filters?.endFormatted}</p>
                                <p className="text-xs text-violet-300 mt-2 flex items-center gap-1">
                                    <Filter className="h-3 w-3" />
                                    {filters?.preset === 'custom' ? 'Custom Range' : PRESETS.find(p => p.key === filters?.preset)?.label || 'Last Month'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ── Charts ───────────────────────────────────────────────────────── */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                                    <BarChart3 className="h-4 w-4 text-indigo-500" />
                                    Hourly Trip Distribution
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">Trip volume by hour of day (aggregated)</p>
                            </div>
                        </div>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} barCategoryGap="12%">
                                    <defs>
                                        <linearGradient id="peakGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#6366f1" stopOpacity={1} />
                                            <stop offset="100%" stopColor="#818cf8" stopOpacity={0.7} />
                                        </linearGradient>
                                        <linearGradient id="normalGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#c7d2fe" stopOpacity={1} />
                                            <stop offset="100%" stopColor="#e0e7ff" stopOpacity={0.6} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="count" name="Trips" radius={[4, 4, 0, 0]}>
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.count === maxCount && maxCount > 0 ? 'url(#peakGradient)' : 'url(#normalGradient)'}
                                                stroke={entry.count === maxCount && maxCount > 0 ? '#6366f1' : 'transparent'}
                                                strokeWidth={entry.count === maxCount && maxCount > 0 ? 2 : 0}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Daily Peak Trend */}
                    {trendData.length > 1 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                                        <TrendingUp className="h-4 w-4 text-violet-500" />
                                        Daily Peak Hour Trend
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-0.5">How the peak hour shifts day by day</p>
                                </div>
                            </div>
                            <div className="h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={trendData}>
                                        <defs>
                                            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
                                        <Tooltip content={<TrendTooltip />} />
                                        <Area type="monotone" dataKey="peakCount" name="Peak Trips" stroke="#8b5cf6" strokeWidth={2.5} fill="url(#trendFill)" dot={{ r: 3, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }} />
                                        <Line type="monotone" dataKey="totalTrips" name="Total Trips" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* ── Daily Breakdown Table ────────────────────────────────── */}
                    {dailyPeakTrend.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100">
                                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-indigo-500" />
                                    Daily Breakdown
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50/80">
                                            <th className="text-left px-5 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Date</th>
                                            <th className="text-left px-5 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Peak Hour</th>
                                            <th className="text-right px-5 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Peak Trips</th>
                                            <th className="text-right px-5 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Simultaneous</th>
                                            <th className="text-right px-5 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Total Trips</th>
                                            <th className="text-right px-5 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Peak %</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {paginatedTrend.map((day, idx) => {
                                            const pct = day.totalTrips > 0
                                                ? ((day.peakCount / day.totalTrips) * 100).toFixed(1)
                                                : '0.0';
                                            return (
                                                <tr key={day._id} className="hover:bg-gray-50/50 transition-colors">
                                                    <td className="px-5 py-3 text-gray-900 font-medium">{day._id}</td>
                                                    <td className="px-5 py-3">
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                                            <Clock className="h-3 w-3" />
                                                            {formatHour(day.peakHour)}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3 text-right font-semibold text-indigo-600">{day.peakCount.toLocaleString()}</td>
                                                    <td className="px-5 py-3 text-right">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                                                            <Layers className="h-3 w-3" />
                                                            {(day.maxConcurrent || 0).toLocaleString()}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3 text-right text-gray-600">{day.totalTrips.toLocaleString()}</td>
                                                    <td className="px-5 py-3 text-right">
                                                        <span className={`text-xs font-semibold ${parseFloat(pct) > 30 ? 'text-amber-600' : 'text-gray-500'}`}>
                                                            {pct}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
                                    <p className="text-xs text-gray-500">
                                        Showing <span className="font-semibold text-gray-900">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(currentPage * ITEMS_PER_PAGE, dailyPeakTrend.length)}</span> of <span className="font-semibold text-gray-900">{dailyPeakTrend.length}</span> days
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                                                // Only show first, last, and pages around current
                                                if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                                                    return (
                                                        <button
                                                            key={p}
                                                            onClick={() => setCurrentPage(p)}
                                                            className={`w-8 h-8 text-xs font-semibold rounded-lg transition-all ${currentPage === p
                                                                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/25'
                                                                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {p}
                                                        </button>
                                                    );
                                                }
                                                if (p === 2 || p === totalPages - 1) {
                                                    return <span key={p} className="text-gray-400">...</span>;
                                                }
                                                return null;
                                            }).filter((item, index, self) =>
                                                // Remove duplicate dots
                                                item?.type !== 'span' || (index > 0 && self[index - 1]?.type !== 'span')
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PeakHours;
